import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Rating as MuiRating,
} from '@mui/material';
import { generateClient } from '@aws-amplify/api';
import { getConversation } from '../../graphql/queries';
import { updateConversation } from '../../graphql/mutations';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const client = generateClient();

const Rating: React.FC = () => {
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get('id');
  const confirmationNumber = searchParams.get('confirmation');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [conversation, setConversation] = useState<any>(null);
  const [rating, setRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>('');

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!conversationId && !confirmationNumber) {
          setError('Missing conversation ID or confirmation number');
          setLoading(false);
          return;
        }

        let conv: any = null;

        // Try to fetch by conversation ID first
        if (conversationId) {
          try {
            // Try with apiKey first (for public access)
            const result: any = await client.graphql({
              query: getConversation,
              variables: { id: conversationId },
              authMode: 'apiKey', // Use API key for public access
            });
            
            // Check for GraphQL errors
            if (result.errors && result.errors.length > 0) {
              console.error('GraphQL errors:', result.errors);
              throw new Error(result.errors[0].message || 'GraphQL error occurred');
            }
            
            conv = result.data?.getConversation;
            
            if (!conv) {
              console.warn('Conversation not found for ID:', conversationId);
              setError('Conversation not found. Please verify the link is correct.');
              setLoading(false);
              return;
            }
          } catch (err: any) {
            console.error('Error fetching conversation by ID with apiKey:', err);
            // If apiKey fails, try with iam
            try {
              const result: any = await client.graphql({
                query: getConversation,
                variables: { id: conversationId },
                authMode: 'iam',
              });
              
              // Check for GraphQL errors
              if (result.errors && result.errors.length > 0) {
                console.error('GraphQL errors (IAM):', result.errors);
                throw new Error(result.errors[0].message || 'GraphQL error occurred');
              }
              
              conv = result.data?.getConversation;
              
              if (!conv) {
                console.warn('Conversation not found for ID (IAM):', conversationId);
                setError('Conversation not found. Please verify the link is correct.');
                setLoading(false);
                return;
              }
            } catch (iamErr: any) {
              console.error('Error fetching conversation with IAM:', iamErr);
              const errorMessage = iamErr.errors?.[0]?.message || iamErr.message || 'Failed to load conversation. Please check the link and try again.';
              setError(errorMessage);
              setLoading(false);
              return;
            }
          }
        }

        // If not found and we have confirmation number, show helpful message
        if (!conv && confirmationNumber) {
          setError('Conversation not found. Please use the link provided in your email.');
          setLoading(false);
          return;
        }

        if (!conv) {
          setError('Conversation not found');
          setLoading(false);
          return;
        }

        // Check if feedback already submitted
        if (conv.feedback && conv.feedbackSubmittedAt) {
          setFeedback(conv.feedback);
          setRating(conv.feedbackScore || 0);
          setSuccess(true);
        }

        setConversation(conv);
      } catch (err: any) {
        console.error('Error fetching conversation:', err);
        setError(err.message || 'Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [conversationId, confirmationNumber]);

  const handleRatingChange = (event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue !== null) {
      setRating(newValue);
      // Map rating to feedback text
      if (newValue === 1) {
        setFeedback('Poor');
      } else if (newValue === 2 || newValue === 3) {
        setFeedback('Fair');
      } else if (newValue === 4 || newValue === 5) {
        setFeedback('Excellent');
      }
    }
  };

  const handleSubmit = async () => {
    if (!rating || !feedback) {
      setError('Please select a rating');
      return;
    }

    if (!conversationId) {
      setError('Missing conversation ID');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Try with apiKey first, fallback to iam
      try {
        await client.graphql({
          query: updateConversation,
          variables: {
            input: {
              id: conversationId,
              feedback: feedback,
              feedbackScore: rating,
              feedbackSubmittedAt: new Date().toISOString(),
            },
          },
          authMode: 'apiKey',
        });
      } catch (err: any) {
        // Fallback to IAM if apiKey fails
        await client.graphql({
          query: updateConversation,
          variables: {
            input: {
              id: conversationId,
              feedback: feedback,
              feedbackScore: rating,
              feedbackSubmittedAt: new Date().toISOString(),
            },
          },
          authMode: 'iam',
        });
      }

      setSuccess(true);
    } catch (err: any) {
      console.error('Error submitting rating:', err);
      setError(err.message || 'Failed to submit rating. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !conversation) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => window.location.href = '/'}>
            Go Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        {success ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Thank You!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your feedback has been submitted successfully.
            </Typography>
            {feedback && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your Rating:
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                  <MuiRating value={rating} readOnly size="large" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {feedback}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
              Rate Your Experience
            </Typography>
            {conversation?.guestName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Hello {conversation.guestName}, we'd love to hear about your experience!
              </Typography>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                How would you rate your experience?
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <MuiRating
                  name="rating"
                  value={rating}
                  onChange={handleRatingChange}
                  size="large"
                  sx={{
                    fontSize: '3rem',
                    '& .MuiRating-iconFilled': {
                      color: 'primary.main',
                    },
                  }}
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarIcon fontSize="inherit" sx={{ opacity: 0.3 }} />}
                />
                {rating > 0 && (
                  <Typography variant="h6" color="primary.main">
                    {feedback}
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={!rating || submitting}
              sx={{
                minWidth: 200,
                py: 1.5,
                fontSize: '1.1rem',
                textTransform: 'none',
              }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Rating'}
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Rating;

